import { useAppDispatch } from '../../../store';
import { applyCopySnapshot } from '../../../store/slices/configTemplateDraftSlice';
import { fetchConfigTemplateStates } from '../../../store/slices/configTemplatesListSlice';
import { copyConfigTemplateFromLive } from '../../../services/configTemplateService';
import {
    CpmButton,
    CpmIconButton,
    CpmModal,
    CpmTooltip,
} from '../../../components/design-system';
import { useState } from 'react';
import type { CpmState } from '../../../types/centralProductMaster.types';
import type {
    ConfigTemplateDraft,
    ConfigTemplateFieldSchema,
    ConfigTemplateLevel,
} from '../../../types/configTemplate.types';
import {
    CONFIG_TEMPLATE_COPY,
    copiedFrom,
    copyConfirmLabel,
    copyFooterNote,
    copyPickerSubtitle,
    copyPickerTipBody,
    copyPickerTitle,
    copyReplaceBody,
} from '../configTemplateCopy';
import { includedAndValuesFromTrees } from '../utils/draftPayload';
import { useTargetPicker } from '../hooks/useTargetPicker';
import TargetPickerList from './TargetPickerList';
import { PickerPulseLine } from './PickerFilterPopover';

interface CopyFromLiveFlowProps {
    open: boolean;
    onClose: () => void;
    level: ConfigTemplateLevel;
    draft: ConfigTemplateDraft;
    fields: ConfigTemplateFieldSchema[];
    states: CpmState[];
    statesStatus?: 'idle' | 'loading' | 'success' | 'error';
    dirty?: boolean;
    onCopied: (message: string) => void;
    onCopyFailed: (source: { id: string; name: string }) => void;
}

function StoreGlyph() {
    return (
        <svg
            width="19"
            height="19"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
        >
            <path d="M3 9.5 12 4l9 5.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M5 10v9h14v-9" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10 19v-6h4v6" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );
}

function CloseIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2">
            <path d="M18 6 6 18M6 6l12 12" strokeLinecap="round" />
        </svg>
    );
}

function InfoIcon() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 16v-4M12 8h.01" strokeLinecap="round" />
        </svg>
    );
}

export default function CopyFromLiveFlow({
    open,
    onClose,
    level,
    draft,
    fields,
    states,
    statesStatus = 'success',
    dirty = false,
    onCopied,
    onCopyFailed,
}: CopyFromLiveFlowProps) {
    const dispatch = useAppDispatch();
    const picker = useTargetPicker({ level, mode: 'copy', open });
    const [busy, setBusy] = useState(false);

    const selected = picker.selectedIds[0] ? picker.selected[picker.selectedIds[0]] : undefined;
    const settingsCount = draft.included.length;
    const needsReplace = settingsCount > 0 || Boolean(draft.copyBaseline);
    const filtering = picker.waitingSearch || picker.status === 'loading';

    const runCopy = async () => {
        if (!selected) return;
        setBusy(true);
        try {
            const snapshot = await copyConfigTemplateFromLive({
                level,
                storeBrandId: level === 'STORE_BRAND' ? selected._id : undefined,
                storeId: level === 'STORE' ? selected._id : undefined,
                templateId: draft.id ?? undefined,
            });
            const mapped = includedAndValuesFromTrees(snapshot, fields);
            dispatch(
                applyCopySnapshot({
                    included: mapped.included,
                    values: mapped.values,
                    sourceName: selected.name,
                })
            );
            setBusy(false);
            onCopied(copiedFrom(selected.name));
            onClose();
        } catch {
            setBusy(false);
            onCopyFailed({ id: selected._id, name: selected.name });
            onClose();
        }
    };

    return (
        <CpmModal
            open={open}
            onClose={onClose}
            maxWidth={660}
            closeOnEscape={!busy}
            closeOnBackdrop={!busy}
        >
            <div className="flex flex-col" style={{ maxHeight: 'calc(100vh - 128px)' }}>
                <div
                    className="flex items-start"
                    style={{
                        gap: 14,
                        padding: '18px 20px 16px',
                        borderBottom: '1px solid var(--cpm-border-default)',
                    }}
                >
                    <div
                        className="flex shrink-0 items-center justify-center"
                        style={{
                            width: 38,
                            height: 38,
                            borderRadius: 10,
                            border: '1px solid var(--cpm-border-strong)',
                            background: '#fff',
                            color: 'var(--cpm-brand-purple)',
                        }}
                    >
                        <StoreGlyph />
                    </div>
                    <div className="min-w-0 flex-1">
                        <div
                            style={{
                                fontSize: 17,
                                fontWeight: 800,
                                letterSpacing: '-0.02em',
                                color: 'var(--cpm-text-primary)',
                            }}
                        >
                            {copyPickerTitle(level)}
                        </div>
                        <div className="mt-1 flex items-center" style={{ gap: 7 }}>
                            <span
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: 'var(--cpm-text-secondary)',
                                }}
                            >
                                {copyPickerSubtitle(level, needsReplace)}
                            </span>
                            <CpmTooltip content={copyPickerTipBody(level)} align="start" fit>
                                <span
                                    className="inline-flex items-center"
                                    style={{ color: 'var(--cpm-text-tertiary)' }}
                                >
                                    <InfoIcon />
                                </span>
                            </CpmTooltip>
                        </div>
                    </div>
                    <CpmIconButton
                        icon={<CloseIcon />}
                        label="Close"
                        size="md"
                        disabled={busy}
                        onClick={onClose}
                    />
                </div>

                <TargetPickerList
                    level={level}
                    picker={picker}
                    states={states}
                    single
                    disabled={busy}
                    statesStatus={statesStatus}
                    onRetryStates={() => void dispatch(fetchConfigTemplateStates())}
                    leading={
                        needsReplace ? (
                            <div style={{ padding: '12px 20px 0' }}>
                                <div
                                    className="flex flex-col"
                                    style={{
                                        gap: 4,
                                        padding: '10px 12px',
                                        borderRadius: 10,
                                        background: 'var(--cpm-warning-bg)',
                                        border: '1px solid var(--cpm-warning-border)',
                                    }}
                                >
                                    <div
                                        style={{
                                            fontSize: 13,
                                            fontWeight: 800,
                                            color: 'var(--cpm-warning-text)',
                                        }}
                                    >
                                        {CONFIG_TEMPLATE_COPY.COPY_REPLACE_BANNER_TITLE}
                                    </div>
                                    <div
                                        style={{
                                            fontSize: 12.5,
                                            fontWeight: 500,
                                            lineHeight: 1.5,
                                            color: 'var(--cpm-warning-text)',
                                        }}
                                    >
                                        {copyReplaceBody(settingsCount, dirty, level)}
                                    </div>
                                </div>
                            </div>
                        ) : null
                    }
                />

                <div
                    className="flex shrink-0 items-center justify-between"
                    style={{
                        gap: 14,
                        padding: '13px 20px',
                        borderTop: '1px solid var(--cpm-border-default)',
                        background: 'rgba(249,249,252,0.88)',
                        backdropFilter: 'blur(14px)',
                        boxShadow: '0 -6px 18px -8px rgba(14,8,88,0.14)',
                    }}
                >
                    <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--cpm-text-secondary)' }}>
                        {copyFooterNote(level, selected?.name)}
                    </span>
                    <div className="flex items-center" style={{ gap: 9 }}>
                        {busy ? <PickerPulseLine>{CONFIG_TEMPLATE_COPY.COPY_COPYING}</PickerPulseLine> : null}
                        <CpmButton variant="outline" size="md" disabled={busy} onClick={onClose}>
                            {CONFIG_TEMPLATE_COPY.COPY_CANCEL}
                        </CpmButton>
                        <CpmButton
                            variant="navy"
                            size="md"
                            disabled={!selected || busy || filtering}
                            isLoading={busy}
                            onClick={() => void runCopy()}
                        >
                            {copyConfirmLabel(level)}
                        </CpmButton>
                    </div>
                </div>
            </div>
        </CpmModal>
    );
}
